import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Search, FileText, Calendar, DollarSign, ArrowRight, ChevronLeft, ChevronRight, Trash2, Users } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ComparisonHistory() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [comparisons, setComparisons] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState('all');
  const limit = 10;

  useEffect(() => {
    if (user?.role === 'admin') {
      axios.get(`${API_URL}/api/users?authorization=${token}`).then(res => setUsers(res.data)).catch(() => {});
    }
  }, [token, user]);

  const fetchComparisons = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ authorization: token, skip: page * limit, limit });
      if (search) params.append('search', search);
      if (filterUser && filterUser !== 'all') params.append('filter_user', filterUser);
      const response = await axios.get(`${API_URL}/api/comparisons?${params}`);
      setComparisons(response.data.comparisons);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load comparisons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(fetchComparisons, 300);
    return () => clearTimeout(debounce);
  }, [token, search, page, filterUser]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/comparisons/${deleteTarget.id}?authorization=${token}`);
      toast.success('Comparison deleted');
      setDeleteTarget(null);
      fetchComparisons();
    } catch (error) {
      toast.error('Failed to delete comparison');
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatCurrency = (amount) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
  const totalPages = Math.ceil(total / limit);

  return (
    <Layout>
      <div className="space-y-6" data-testid="comparison-history-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">SME Comparison History</h1>
            <p className="text-slate-600">{total} total comparisons</p>
          </div>
          <Link to="/new-comparison">
            <Button className="rounded-full bg-primary hover:bg-primary/90">New SME Comparison</Button>
          </Link>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="flex gap-4 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input placeholder="Search by customer name, NMI, or report ID..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-10 h-12" data-testid="search-input" />
              </div>
              {user?.role === 'admin' && users.length > 0 && (
                <Select value={filterUser} onValueChange={(v) => { setFilterUser(v); setPage(0); }}>
                  <SelectTrigger className="w-[200px] h-12" data-testid="filter-user-select">
                    <Users className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.filter(u => u.status === 'approved').map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div></div>
            ) : comparisons.length > 0 ? (
              <div className="divide-y">
                {comparisons.map((comparison) => (
                  <div key={comparison.id} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors" data-testid={`comparison-item-${comparison.id}`}>
                    <Link to={`/comparison/${comparison.id}`} className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-slate-900 truncate">{comparison.extracted_data?.customer_name || 'Unknown Customer'}</p>
                          <Badge variant="outline" className="text-xs font-mono">{comparison.report_id}</Badge>
                          {comparison.quote_status === 'accepted' && <Badge className="bg-green-100 text-green-700 text-xs">Accepted</Badge>}
                          {comparison.quote_status === 'sent' && <Badge className="bg-blue-100 text-blue-700 text-xs">Quote Sent</Badge>}
                          {comparison.outcome === 'won' && <Badge className="bg-green-100 text-green-800 text-xs border border-green-300">Won</Badge>}
                          {comparison.outcome === 'lost' && <Badge className="bg-red-100 text-red-700 text-xs border border-red-300">Lost</Badge>}
                          {comparison.source === 'customer_portal' && <Badge className="bg-purple-100 text-purple-700 text-xs">Portal</Badge>}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 flex-wrap">
                          {comparison.extracted_data?.nmi && <span className="font-mono text-xs">NMI: {comparison.extracted_data.nmi}</span>}
                          <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(comparison.created_at)}</span>
                          <span>by {comparison.created_by_name}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`font-semibold ${comparison.comparison_result?.savings_amount > 0 ? 'text-green-600' : 'text-slate-600'}`}>
                          <DollarSign className="w-4 h-4 inline" />{formatCurrency(comparison.comparison_result?.savings_amount)}
                        </p>
                        <p className="text-xs text-slate-500">{comparison.comparison_result?.savings_percentage?.toFixed(1)}% savings</p>
                      </div>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: comparison.id, name: comparison.extracted_data?.customer_name || 'Unknown', reportId: comparison.report_id }); }}
                      className="text-slate-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                      data-testid={`delete-comparison-${comparison.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Link to={`/comparison/${comparison.id}`}><ArrowRight className="w-5 h-5 text-slate-400 flex-shrink-0" /></Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{search ? 'No comparisons found matching your search' : 'No comparisons yet'}</p>
                {!search && <Link to="/new-comparison"><Button className="mt-4 rounded-full">Create Your First Comparison</Button></Link>}
              </div>
            )}
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} data-testid="prev-page-btn"><ChevronLeft className="w-4 h-4" />Previous</Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} data-testid="next-page-btn">Next<ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}

        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Comparison</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the comparison for {deleteTarget?.name} ({deleteTarget?.reportId})? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-comparison-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-comparison-confirm">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
